import React, { useState, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import GenericDraggableTableView from "../../../../Features/Table/GenericDraggableTable";
import { getColumnsConfig } from "../../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import { MAX_COLUMN_SELECTION_COUNT } from "../../../../Helper/DigitalTwin/DataFile";
import { usePagesContent } from "../../../../Hooks/usePagesContent";

const ChartDrillDownTable = ({
  tableConfigs,
  getTableData,
  applyTableAction,
  getCSVFileName,
  selectedDataPoint,
  pageSize = 10,
  customRenderers = {},
  isOpen = true,
  disableColumnSettings = false,
}) => {
  const pagesContent = usePagesContent();

  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);
  const [sortBy, setSortBy] = useState([]);

  // Table column management
  const [initialColumns, setInitialColumns] = useState([]);
  const [tableColumns, setTableColumns] = useState([]);
  const [selectedColumnKeys, setSelectedColumnKeys] = useState([]);
  const [defaultSelectedColumnKeys, setDefaultSelectedColumnKeys] = useState([]);
  const [totalSelectedRowKeys, setTotalSelectedRowKeys] = useState(new Set());
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [columnOrder, setColumnOrder] = useState([]);

  // Tracks previous getTableData ref to detect external filter/search changes
  const prevGetTableDataRef = useRef(null);
  // Suppresses the redundant re-fetch triggered by setPageNumber(1) after an external change
  const skipNextPageFetchRef = useRef(false);

  // Reset state when component unmounts or isOpen changes
  useEffect(() => {
    if (!isOpen) {
      setTableData([]);
      setPageNumber(1);
      setRowsPerPage(pageSize);
      setTotalSelectedRowKeys(new Set());
      setIsSelectAll(false);
      setSortBy((prev) => (prev.length === 0 ? prev : []));
    }
  }, [isOpen, pageSize]);

  // Reset pagination and sorting when selectedDataPoint changes
  useEffect(() => {
    if (isOpen && selectedDataPoint) {
      setPageNumber(1);
      setSortBy((prev) => (prev.length === 0 ? prev : []));
    }
  }, [selectedDataPoint, isOpen]);

  // Setup table columns
  useEffect(() => {
    if (!tableConfigs?.tableColumnsConfig || !tableConfigs?.tableColumnOrder) {
      return;
    }

    const availableColumns = getColumnsConfig({
      tableColumns: tableConfigs.tableColumnsConfig,
      tableColumnOrder: tableConfigs.tableColumnOrder,
      customRenderers: customRenderers,
    });

    setInitialColumns(availableColumns);
    const defaultSelected = availableColumns.slice(0, MAX_COLUMN_SELECTION_COUNT);
    const defaultKeys = defaultSelected.map((col) => col.dataIndex);
    setTableColumns(defaultSelected);
    setSelectedColumnKeys(defaultKeys);
    setDefaultSelectedColumnKeys(defaultKeys);
    setColumnOrder(defaultSelected.map((col) => col.headerLabel));
  }, [tableConfigs, customRenderers]);

  // Fetch table data
  useEffect(() => {
    if (!isOpen || !getTableData) return;

    // Detect external filter/search changes by comparing getTableData reference.
    // When it changes and the user was on page > 1, we must fetch from page 1 and
    // suppress the follow-up fetch that setPageNumber(1) would trigger.
    const isExternalChange = prevGetTableDataRef.current !== getTableData;
    prevGetTableDataRef.current = getTableData;

    if (skipNextPageFetchRef.current) {
      skipNextPageFetchRef.current = false;
      return;
    }

    let effectivePage = pageNumber;
    if (isExternalChange && pageNumber !== 1) {
      effectivePage = 1;
      skipNextPageFetchRef.current = true;
      setPageNumber(1);
      setTotalSelectedRowKeys(new Set());
      setIsSelectAll(false);
    } else if (isExternalChange) {
      setTotalSelectedRowKeys(new Set());
      setIsSelectAll(false);
    }

    const fetchData = async () => {
      try {
        setIsLoading(true);
        const result = await getTableData(selectedDataPoint, effectivePage, sortBy);
        setTableData(result.data || []);
        setTotal(result.total || 0);
      } catch (error) {
        console.error("Error fetching table data:", error);
        setTableData([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [isOpen, getTableData, pageNumber, selectedDataPoint, sortBy]);

  // Handle page change - updates pageNumber which triggers useEffect to fetch data
  const handlePageChange = (page) => {
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
    setPageNumber(page);
  };

  // Handle sort change
  const handleSortChange = (key, order) => {
    let columnKey = key;
    if (tableConfigs?.tableColumnsConfig?.[key]?.sortKey) {
      columnKey = tableConfigs?.tableColumnsConfig?.[key]?.sortKey;
    }

    const direction = order === "asc" ? "ASC" : "DESC";
    const newSortBy = [{ column: columnKey, direction }];
    setSortBy(newSortBy);
    setPageNumber(1);
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
  };

  // Get current sorting state for initialSorting prop
  const getCurrentSorting = () => {
    if (sortBy && sortBy.length > 0) {
      const firstSort = sortBy[0];
      return {
        sortBy: firstSort.column,
        orderBy: firstSort.direction.toLowerCase()
      };
    }
    return {};
  };

  // Handle row selection
  const onSelectChange = (record, selected) => {
    const updatedSelectedKeys = new Set(totalSelectedRowKeys);
    if (selected) {
      updatedSelectedKeys.add(record.id);
    } else {
      updatedSelectedKeys.delete(record.id);
      if (isSelectAll) {
        setIsSelectAll(false);
      }
    }
    setTotalSelectedRowKeys(updatedSelectedKeys);
  };

  // Handle select all
  const onSelectAllChange = (selected, selectedRows) => {
    setIsSelectAll(selected);
    setTotalSelectedRowKeys(
      selected ? new Set(selectedRows.map((row) => row?.id)) : new Set()
    );
  };

  // Handle column order change
  const onColumnOrderChange = (newTableColumns) => {
    setColumnOrder(newTableColumns.map((col) => col.headerLabel));
  };

  // Apply selected columns
  const applySelectedColumns = (newSelectedKeys) => {
    const filteredColumns = initialColumns.filter((columnConfig) =>
      newSelectedKeys.includes(columnConfig.dataIndex)
    );

    setTableColumns(filteredColumns);
    setSelectedColumnKeys(newSelectedKeys);
    setColumnOrder(filteredColumns.map((col) => col.headerLabel));
  };

  // Handle clear selection
  const handleClearSelection = () => {
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
  };

  // Wrapper function for CSV download
  const getCSVData = async () => {
    if (!applyTableAction) {
      return [];
    }
    const rowSelectionKeys = Array.from(totalSelectedRowKeys);
    const params = {
      columns: tableColumns,
      columnOrder,
    };
    return await applyTableAction(rowSelectionKeys, "download", isSelectAll, params);
  };

  // Wrapper function for email
  const onSendEmail = async ({ sendTo, sendCc, subject, body }) => {
    if (!applyTableAction) {
      return;
    }
    const rowSelectionKeys = Array.from(totalSelectedRowKeys);
    const params = {
      columns: tableColumns,
      columnOrder,
      emailTo: sendTo,
      emailCc: sendCc,
      subject: subject,
      body: body,
    };
    return await applyTableAction(rowSelectionKeys, "mail", isSelectAll, params);
  };

  // Get table title
  const tableTitle = typeof tableConfigs?.title === "function"
    ? tableConfigs.title(selectedDataPoint)
    : tableConfigs?.title || "Table";


  return (
    <Box sx={{ flex: 1 }}>
      <GenericDraggableTableView
        title={tableTitle}
        showPrimaryButton={false}
        count={total || tableData.length}
        tableColumns={tableColumns}
        selectedColumns={selectedColumnKeys}
        tableData={tableData}
        availableEmailDomains={pagesContent?.metaInfo?.globalConfig?.["email.domains"] || []}
        initialColumns={initialColumns}
        applySelectedColumns={applySelectedColumns}
        defaultSelectedColumns={defaultSelectedColumnKeys}
        isLoading={isLoading}
        disablePagination={false}
        pageNumber={pageNumber}
        rowsPerPage={rowsPerPage}
        total={total !== undefined ? total : tableData.length}
        disableSearch={true}
        disableFiltersSettings={true}
        disableColumnSettings={disableColumnSettings}
        columnOrder={columnOrder}
        onColumnOrderChange={onColumnOrderChange}
        applySort={handleSortChange}
        initialSorting={getCurrentSorting()}
        emptyMessage={tableConfigs?.emptyMessage || "No data available"}
        containerStyles={{
          backgroundColor: "#FFFFFF",
          borderRadius: "8px",
          border: "1px solid #E6E6E6",
        }}
        getTotalRowSelection={() => Array.from(totalSelectedRowKeys)}
        getTotalRowSelectionCount={() =>
          isSelectAll ? (total || tableData.length) : totalSelectedRowKeys.size
        }
        rowSelection={{
          selectedRowKeys: Array.from(totalSelectedRowKeys),
          onSelect: onSelectChange,
          onSelectAll: onSelectAllChange,
        }}
        onClearSelection={handleClearSelection}
        onPageChange={handlePageChange}
        getCSVData={getCSVData}
        getCSVFileName={getCSVFileName}
        onSendEmail={onSendEmail}
      />
    </Box>
  );
};

export default ChartDrillDownTable;
